import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Calendar, CalendarRange, Clock, ArrowLeft, ArrowRight, CheckCircle2,
  Loader2, Package, Euro, MapPin, Info, AlertCircle, Users, ShieldCheck, User,
} from "lucide-react";
import { useSettings } from "../../lib/useSettings";
import { useCustomerAuth } from "../../lib/useCustomerAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(cents, currency = "EUR") {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format((cents || 0) / 100);
}
function pad(n) { return n.toString().padStart(2, "0"); }
function toYMD(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function addDays(d, n) { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; }
function unitLabel(u) { return u === "hour" ? "heure" : u === "night" ? "nuit" : "jour"; }
function pluralUnit(u, n) { return n > 1 ? unitLabel(u) + "s" : unitLabel(u); }

// ============ LIST PAGE ============
export function RentalsListPage() {
  const { settings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ products: [], enabled: false });

  useEffect(() => {
    axios.get(`${API}/rentals`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const currency = settings?.currency || "EUR";

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--brand-sky)]"/></div>;

  if (!data.enabled) {
    return (
      <section className="py-16 px-4 text-center max-w-2xl mx-auto">
        <CalendarRange className="w-12 h-12 mx-auto opacity-40 mb-4 text-[color:var(--brand-navy)]"/>
        <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Location</h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-2">Le module de location n'est pas encore activé.</p>
      </section>
    );
  }

  return (
    <section className="py-10 md:py-14 px-4" data-testid="rentals-list-page">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold">Location</div>
          <h1 className="font-display text-4xl md:text-5xl font-black text-[color:var(--brand-navy)] mt-1">Nos locations</h1>
          {data.intro_text && <p className="text-[hsl(var(--muted-foreground))] mt-3 max-w-3xl">{data.intro_text}</p>}
        </header>

        {data.products.length === 0 ? (
          <Card className="p-10 text-center text-[hsl(var(--muted-foreground))]">
            <Package className="w-10 h-10 mx-auto opacity-40 mb-3"/>
            <p>Aucune location disponible pour le moment.</p>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.products.map(p => {
              const price = p.rental_price_cents || p.price_cents || 0;
              const u = p.rental_unit || "day";
              const unitsCount = (p.rental_units || []).length;
              return (
                <Link key={p.id} to={`/rentals/${p.slug || p.id}`} className="group" data-testid={`rental-card-${p.id}`}>
                  <Card className="overflow-hidden h-full transition-all hover:shadow-lg hover:-translate-y-0.5">
                    <div className="aspect-video bg-[hsl(var(--muted))] overflow-hidden">
                      {p.featured_image ? (
                        <img src={p.featured_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                      ) : <div className="flex items-center justify-center h-full"><Package className="w-10 h-10 opacity-30"/></div>}
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)] group-hover:text-[color:var(--brand-sky)] transition-colors">{p.name}</h3>
                      {p.short_description && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{p.short_description}</p>}
                      <div className="flex items-center justify-between mt-4">
                        <div>
                          <div className="font-display text-2xl font-black text-[color:var(--brand-navy)]">{money(price, currency)}<span className="text-sm font-normal text-[hsl(var(--muted-foreground))]"> / {unitLabel(u)}</span></div>
                        </div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Users className="w-3 h-3"/>{unitsCount} unité{unitsCount > 1 ? "s" : ""}</div>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ============ DETAIL / BOOKING PAGE ============
export function RentalDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { token, customer } = useCustomerAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // date picker state
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD or ISO
  const [endDate, setEndDate] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");  // "" = auto
  const [availability, setAvailability] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", customer_notes: "" });
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [accountMode, setAccountMode] = useState("guest"); // guest | create | existing
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const currency = settings?.currency || "EUR";

  useEffect(() => {
    axios.get(`${API}/rentals/${slug}`)
      .then(r => setProduct(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Produit introuvable"))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (customer) {
      setForm(f => ({
        ...f,
        customer_name: f.customer_name || `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
        customer_email: f.customer_email || customer.email || "",
        customer_phone: f.customer_phone || customer.phone || "",
      }));
      setAccountMode("guest");
    }
  }, [customer]);

  const unit = product?.rental_unit || "day";
  const unitPrice = product?.rental_price_cents || product?.price_cents || 0;

  const duration = useMemo(() => {
    if (!startDate || !endDate) return 0;
    try {
      const s = new Date(startDate);
      const e = new Date(endDate);
      if (e <= s) return 0;
      if (unit === "hour") return Math.max(1, Math.round((e - s) / 3600000));
      return Math.max(1, Math.round((e - s) / 86400000));
    } catch { return 0; }
  }, [startDate, endDate, unit]);

  const totalPrice = duration * unitPrice;

  // load availability when dates change
  useEffect(() => {
    if (!product || !startDate || !endDate || duration < 1) { setAvailability(null); return; }
    const start = toYMD(new Date(startDate));
    const end = toYMD(addDays(new Date(endDate), 1));
    axios.get(`${API}/rentals/${product.id}/availability?start=${start}&end=${end}`)
      .then(r => setAvailability(r.data))
      .catch(() => setAvailability(null));
  }, [product, startDate, endDate, duration]);

  const freeUnits = useMemo(() => {
    if (!availability || !startDate || !endDate) return product?.rental_units || [];
    const s = new Date(startDate).getTime();
    const e = new Date(endDate).getTime();
    return (availability.units || []).filter(u => {
      return !(u.busy || []).some(b => {
        try {
          const bs = new Date(b.start).getTime();
          const be = new Date(b.end).getTime();
          return bs < e && be > s;
        } catch { return false; }
      });
    });
  }, [availability, startDate, endDate, product]);

  const canBook = duration >= (product?.rental_min_duration || 1) && duration <= (product?.rental_max_duration || 30)
    && form.customer_name && form.customer_email && cgvAccepted
    && freeUnits.length > 0;

  const submit = async () => {
    if (!canBook) return;
    setSubmitting(true);
    try {
      // build ISO with time if hour unit ; else YYYY-MM-DD + checkin/checkout hours for night
      let start_at = startDate;
      let end_at = endDate;
      if (unit === "night") {
        const ci = product.rental_checkin_time || "15:00";
        const co = product.rental_checkout_time || "11:00";
        start_at = `${toYMD(new Date(startDate))}T${ci}:00`;
        end_at = `${toYMD(new Date(endDate))}T${co}:00`;
      } else if (unit === "day") {
        start_at = `${toYMD(new Date(startDate))}T09:00:00`;
        end_at = `${toYMD(new Date(endDate))}T18:00:00`;
      }
      const payload = {
        product_id: product.id,
        unit_id: selectedUnitId || undefined,
        start_at,
        end_at,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        customer_notes: form.customer_notes,
        cgv_accepted: cgvAccepted,
        origin_url: window.location.origin,
      };
      if (accountMode === "create") {
        if (!password || password.length < 8) { toast.error("Mot de passe min. 8 caractères"); setSubmitting(false); return; }
        payload.create_account = true;
        payload.create_account_password = password;
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/rentals/book`, payload, { headers });
      if (data.stripe_url) {
        window.location.href = data.stripe_url;
      } else {
        toast.success("Réservation confirmée !");
        navigate(`/rental/confirmation?rental_id=${data.rental_id}&email=${encodeURIComponent(form.customer_email)}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de réservation");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></div>;
  if (error || !product) return (
    <section className="py-20 text-center">
      <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-3"/>
      <p>{error}</p>
      <Link to="/rentals"><Button className="mt-4"><ArrowLeft className="w-4 h-4 mr-1"/>Retour</Button></Link>
    </section>
  );

  const inputType = unit === "hour" ? "datetime-local" : "date";
  const minStart = unit === "hour"
    ? new Date().toISOString().slice(0, 16)
    : toYMD(new Date());

  return (
    <section className="py-10 md:py-14 px-4" data-testid="rental-detail-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/rentals" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-sky)] inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4"/>Toutes les locations</Link>

        <div className="grid md:grid-cols-[1.3fr_1fr] gap-8">
          {/* LEFT — Product */}
          <div className="space-y-4">
            <div className="aspect-[16/10] bg-[hsl(var(--muted))] rounded-xl overflow-hidden">
              {product.featured_image
                ? <img src={product.featured_image} alt={product.name} className="w-full h-full object-cover"/>
                : <div className="flex items-center justify-center h-full"><Package className="w-16 h-16 opacity-30"/></div>}
            </div>
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-black text-[color:var(--brand-navy)]">{product.name}</h1>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                <span className="font-display text-2xl font-black text-[color:var(--brand-sky)]">{money(unitPrice, currency)}<span className="text-sm font-normal text-[hsl(var(--muted-foreground))]"> / {unitLabel(unit)}</span></span>
                <span className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Users className="w-3.5 h-3.5"/>{(product.rental_units || []).length} unité{(product.rental_units || []).length > 1 ? "s" : ""}</span>
                {product.rental_deposit_cents > 0 && <span className="text-sm text-amber-700 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5"/>Caution {money(product.rental_deposit_cents, currency)}</span>}
              </div>
            </div>
            {product.short_description && <p className="text-[hsl(var(--muted-foreground))]">{product.short_description}</p>}
            {product.rental_description && (
              <Card className="p-4 bg-[hsl(var(--muted))]/30 border-[color:var(--brand-sky)]/20">
                <div className="flex gap-2">
                  <Info className="w-4 h-4 text-[color:var(--brand-sky)] shrink-0 mt-0.5"/>
                  <div className="text-sm whitespace-pre-line">{product.rental_description}</div>
                </div>
              </Card>
            )}
          </div>

          {/* RIGHT — Booking */}
          <Card className="p-5 h-fit sticky top-6" data-testid="rental-booking-form">
            <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)] mb-4">Réserver</h2>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{unit === "night" ? "Arrivée" : "Début"}</Label>
                  <Input type={inputType} min={minStart} value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="rental-start"/>
                </div>
                <div>
                  <Label className="text-xs">{unit === "night" ? "Départ" : "Fin"}</Label>
                  <Input type={inputType} min={startDate || minStart} value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="rental-end"/>
                </div>
              </div>

              {duration > 0 && (
                <div className="text-sm p-3 rounded-lg bg-[color:var(--brand-sky)]/5 border border-[color:var(--brand-sky)]/20">
                  <div className="flex items-center justify-between">
                    <span className="text-[hsl(var(--muted-foreground))]">Durée</span>
                    <span className="font-semibold">{duration} {pluralUnit(unit, duration)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[hsl(var(--muted-foreground))]">Total</span>
                    <span className="font-display text-lg font-black text-[color:var(--brand-navy)]">{money(totalPrice, currency)}</span>
                  </div>
                </div>
              )}

              {startDate && endDate && duration > 0 && (
                <div>
                  <Label className="text-xs">Unité disponible</Label>
                  {freeUnits.length === 0 ? (
                    <div className="text-sm text-red-600 flex items-center gap-1 mt-1"><AlertCircle className="w-4 h-4"/>Aucune unité disponible sur cette période</div>
                  ) : (
                    <div className="grid gap-1.5 mt-1">
                      <button type="button" onClick={() => setSelectedUnitId("")} className={`text-left text-sm px-3 py-2 rounded border ${!selectedUnitId ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5 font-semibold" : "border-[hsl(var(--border))]"}`}>
                        Auto-assignation ({freeUnits.length} libre{freeUnits.length > 1 ? "s" : ""})
                      </button>
                      {freeUnits.map(u => (
                        <button key={u.id} type="button" onClick={() => setSelectedUnitId(u.id)}
                          className={`text-left text-sm px-3 py-2 rounded border ${selectedUnitId === u.id ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5 font-semibold" : "border-[hsl(var(--border))]"}`}
                          data-testid={`rental-pick-unit-${u.id}`}>
                          {u.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="pt-2 border-t border-[hsl(var(--border))]">
                {/* Account mode switcher */}
                {!customer && (
                  <div className="grid grid-cols-3 gap-1 mb-3 p-1 bg-[hsl(var(--muted))] rounded-lg text-xs">
                    {[{k: "guest", l: "Sans compte"}, {k: "create", l: "Créer un compte"}, {k: "existing", l: "Me connecter"}].map(x => (
                      <button key={x.k} type="button" onClick={() => x.k === "existing" ? navigate("/account/login?redirect=" + encodeURIComponent(window.location.pathname)) : setAccountMode(x.k)}
                        className={`px-2 py-1.5 rounded ${accountMode === x.k ? "bg-white shadow-sm font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}
                        data-testid={`rental-account-${x.k}`}>
                        {x.l}
                      </button>
                    ))}
                  </div>
                )}
                <Input placeholder="Nom complet" value={form.customer_name} onChange={(e) => setForm(f => ({...f, customer_name: e.target.value}))} className="mb-2" data-testid="rental-name"/>
                <Input placeholder="Email" type="email" value={form.customer_email} onChange={(e) => setForm(f => ({...f, customer_email: e.target.value}))} className="mb-2" data-testid="rental-email"/>
                <Input placeholder={product.rental_require_phone ? "Téléphone *" : "Téléphone"} value={form.customer_phone} onChange={(e) => setForm(f => ({...f, customer_phone: e.target.value}))} className="mb-2" data-testid="rental-phone"/>
                {accountMode === "create" && !customer && (
                  <Input placeholder="Créer un mot de passe (min. 8 car.)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mb-2" data-testid="rental-password"/>
                )}
                <Textarea placeholder="Remarques (optionnel)" rows={2} value={form.customer_notes} onChange={(e) => setForm(f => ({...f, customer_notes: e.target.value}))} data-testid="rental-notes"/>
              </div>

              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <Checkbox checked={cgvAccepted} onCheckedChange={setCgvAccepted} data-testid="rental-cgv"/>
                <span>J'accepte les <Link to="/cgv" className="underline">CGV</Link> et la politique de <Link to="/confidentialite" className="underline">confidentialité</Link>.</span>
              </label>

              <Button className="w-full" size="lg" disabled={!canBook || submitting} onClick={submit} data-testid="rental-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
                {totalPrice > 0 ? `Réserver — ${money(totalPrice, currency)}` : "Confirmer la réservation"}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}

// ============ CONFIRMATION PAGE ============
export function RentalConfirmationPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const rentalId = params.get("rental_id");
  const email = params.get("email");
  const [state, setState] = useState({ loading: true, status: null, attempts: 0 });

  useEffect(() => {
    let stop = false;
    const poll = async (n = 0) => {
      if (stop) return;
      try {
        if (sessionId) {
          const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
          setState({ loading: false, status: data.payment_status, attempts: n + 1, data });
          if (data.payment_status === "paid" || data.status === "expired" || n >= 10) return;
          setTimeout(() => poll(n + 1), 2000);
        } else if (rentalId && email) {
          const { data } = await axios.get(`${API}/rentals/lookup/${rentalId}?email=${encodeURIComponent(email)}`);
          setState({ loading: false, status: "free", data });
        }
      } catch (e) {
        setState({ loading: false, status: "error", attempts: n + 1 });
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, rentalId, email]);

  return (
    <section className="py-16 px-4 max-w-2xl mx-auto text-center" data-testid="rental-confirmation">
      <Card className="p-8">
        {state.loading ? (
          <>
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-[color:var(--brand-sky)]"/>
            <p className="mt-4">Vérification du paiement…</p>
          </>
        ) : state.status === "paid" || state.status === "free" ? (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-emerald-600"/></div>
            <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)] mt-4">Réservation confirmée !</h1>
            <p className="text-[hsl(var(--muted-foreground))] mt-2">Un email de confirmation vous a été envoyé. Nous vous attendons bientôt !</p>
            <Link to="/rentals"><Button className="mt-6">Voir d'autres locations</Button></Link>
          </>
        ) : (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-amber-500"/>
            <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mt-3">Paiement non confirmé</h1>
            <p className="text-sm mt-2">Si vous avez payé, vous recevrez un email. Sinon, réessayez.</p>
            <Link to="/rentals"><Button className="mt-6" variant="outline">Retour</Button></Link>
          </>
        )}
      </Card>
    </section>
  );
}
